import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  AllowanceEntity,
  BokkzAlgDataService,
  BokkzClient,
  BokkzTslDataService,
  BokkzTslLogic,
  ClientEntity,
  GezinEninkomenPerMaand,
  Kinderopvangtoeslag,
  PersoonToeslagen,
  Toeslagen
} from "@snelstart-ventures/fiscaal/domain-logic";
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {filter, Subject, takeUntil} from "rxjs";
import {Event, NavigationEnd, Router} from "@angular/router";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import { SharedUiTooltipModule } from '@snelstart/ui-tooltip';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-inkomen',
  templateUrl: './inkomen.component.html',
  styleUrls: ['./inkomen.component.scss'],
    imports: [
        SharedUiFormControlsModule,
        SharedUiDetailsModule,
        CommonModule,
        SharedUiTooltipModule
    ]
})
export class InkomenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private destroy$ = new Subject<void>();
  public clientEntity ={} as ClientEntity;
  public allowanceEntity = {} as AllowanceEntity
  private isSaving = false;
  private focusedElementId: string | null = null;
  public hasSstIb = false;
  public inkomenFg = {} as FormGroup;
  public inkomenListFg = {} as FormGroup;
  public toeslagenFg = {} as FormGroup;
  public kotFg = {} as FormGroup;
  public year: any;
  public formStructure: any;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];

  constructor(
    private router: Router,
    public tslLogic: BokkzTslLogic,
    public dataService: BokkzTslDataService,
    private algDataService: BokkzAlgDataService,
    public bokkzClient: BokkzClient,
    public fb: FormBuilder) {
    this.toeslagenFg = this.fb.group<Toeslagen>({
      berekeningsjaar: null as unknown as number,
      bsn: null as unknown as string,
      geboortedatumAanvrager: null as unknown as Date,
      aanvrager: this.fb.group<PersoonToeslagen>({
        voorletters: null as unknown as string,
        tussenvoegsels: null as unknown as string,
        achternaam: null as unknown as string
      })
    })
    this.inkomenFg = this.fb.group<GezinEninkomenPerMaand>({
      maand: null as unknown as number,
      toeslagPartner: null as unknown as boolean,
      geboortedatumPartner: null as unknown as Date,
      bsnPartner: null as unknown as string,
      toetsinkomenAangever: null as unknown as number,
      toetsinkomenPartner: null as unknown as number,
      toetsvermogenAangever: null as unknown as number,
      toetsvermogenPartner: null as unknown as number,
      aangeverHeeftZorgverzekering: true,
      partnerHeeftZorgverzekering: null as unknown as boolean,
    })
    this.kotFg = this.fb.group<Kinderopvangtoeslag>({
      aantalWerkurenPerWeekEersteOuder: 40,
      aantalWerkurenPerWeekTweedeOuder: 40,
      eersteOuderStudeert: false,
      tweedeOuderStudeert: false,
      alOntvangenKOT: null as unknown as number
    })
    this.inkomenListFg = this.fb.group({
      inkomensgegevens: this.fb.array<GezinEninkomenPerMaand>([])
    });
  }

  get inkomensgegevens(): FormArray {
    return this.inkomenListFg.get('inkomensgegevens') as FormArray;
  }

  createFormGroup(gez?: GezinEninkomenPerMaand, int?: number): FormGroup {
    return this.fb.group<GezinEninkomenPerMaand>({
      maand: gez ? gez.maand : int ? int+1 : 1,
      toeslagPartner: gez ? gez.toeslagPartner : null as unknown as boolean,
      geboortedatumPartner: gez ? gez.geboortedatumPartner : null as unknown as Date,
      bsnPartner: gez ? gez.bsnPartner : null as unknown as string,
      toetsinkomenAangever: gez ? gez.toetsinkomenAangever : null as unknown as number,
      toetsinkomenPartner: gez ? gez.toetsinkomenPartner : null as unknown as number,
      toetsvermogenAangever: gez ? gez.toetsvermogenAangever : null as unknown as number,
      toetsvermogenPartner: gez ? gez.toetsvermogenPartner : null as unknown as number,
      aangeverHeeftZorgverzekering: gez ? gez.aangeverHeeftZorgverzekering : true,
      partnerHeeftZorgverzekering: gez ? gez.partnerHeeftZorgverzekering : null as unknown as boolean,
    });
  }

  public ngOnInit(): void {
    this.inkomenFg.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.formStructure = value;
    });
    this.dataService.allowanceEntity.pipe(takeUntil(this.destroy$)).subscribe((allowanceEntity) => {
      this.allowanceEntity = allowanceEntity;
      this.year = allowanceEntity.year;
      if (this.year) {
        if (allowanceEntity.toeslagen) {
          this.toeslagenFg.patchValue(allowanceEntity.toeslagen, {emitEvent: false});
          if (allowanceEntity.toeslagen!.inkomensgegevens?.at(0)) {
            this.inkomenFg.patchValue(allowanceEntity.toeslagen!.inkomensgegevens[0], {emitEvent: false})
            this.updateForm();
          }
          else{
            this.updateForm();
          }
        }
      }
    });
    this.algDataService.client.pipe(takeUntil(this.destroy$)).subscribe((clientEntity)=> {
      this.clientEntity = clientEntity;
    });
    this.dataService.hasSstIb.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.hasSstIb = value;
    })
    this.router.events.pipe(
      takeUntil(this.destroy$),
      filter(
          (event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.handleUrlChange();
      });
  }

  private updateForm(): void {
    const lijst = this.allowanceEntity?.toeslagen?.inkomensgegevens || [];
    const formArray = this.inkomenListFg.get('inkomensgegevens') as FormArray;
    const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;
    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < 12; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        const waarde= i+1;
        control.setValue({
          toeslagPartner: null as unknown as string,
          geboortedatumPartner: null as unknown as Date,
          bsnPartner: null as unknown as string,
          toetsinkomenAangever:  null as unknown as string,
          toetsinkomenPartner: null as unknown as string,
          toetsvermogenAangever: null as unknown as string,
          toetsvermogenPartner: null as unknown as string,
          aangeverHeeftZorgverzekering: null as unknown as string,
          partnerHeeftZorgverzekering: null as unknown as string,
          maand: waarde})
        control.patchValue(lijst[i]);
      } else {
        const formGroup = this.createFormGroup(lijst[i], i)
        formArray.push(formGroup);
      }
    }
    if (focusedControl && document.contains(focusedControl)) {
      focusedControl.focus();
    }
  }

  public saveForm(): void {
    setTimeout(()=>{
      const formArray = this.inkomenListFg.get('inkomensgegevens') as FormArray;
      for (let i = 0; i < formArray.length; i++) {
        formArray.at(i).value.toeslagPartner = this.inkomenFg.getRawValue().toeslagPartner;
        formArray.at(i).value.toetsinkomenAangever = this.inkomenFg.getRawValue().toetsinkomenAangever;
        formArray.at(i).value.toetsvermogenAangever = this.inkomenFg.getRawValue().toetsvermogenAangever;
        formArray.at(i).value.aangeverHeeftZorgverzekering = this.inkomenFg.getRawValue().aangeverHeeftZorgverzekering;
        if (formArray.at(i).value.toeslagPartner) {
          formArray.at(i).value.partnerHeeftZorgverzekering = true;
        }
      }

      this.inkomenListFg.updateValueAndValidity();
      this.toeslagenFg.updateValueAndValidity();
      this.kotFg.updateValueAndValidity();

      this.inkomenListFg.markAsDirty();
      this.toeslagenFg.markAsDirty();
      this.kotFg.markAsDirty();

      this.allowanceEntity.toeslagen = {
        ...this.allowanceEntity.toeslagen,
        geboortedatumAanvrager: this.toeslagenFg.value.geboortedatumAanvrager,
        bsn: this.toeslagenFg.value.bsn,
        aanvrager: this.toeslagenFg.value.aanvrager,
        inkomensgegevens: formArray.value,
        kot: {
          aantalWerkurenPerWeekEersteOuder: this.kotFg.value.aantalWerkurenPerWeekEersteOuder,
          aantalWerkurenPerWeekTweedeOuder: this.kotFg.value.aantalWerkurenPerWeekTweedeOuder,
          eersteOuderStudeert: this.kotFg.value.eersteOuderStudeert,
          tweedeOuderStudeert: this.kotFg.value.tweedeOuderStudeert,
        }
      } as AllowanceEntity
      this.dataService.updateAllowanceTaxEntity(this.allowanceEntity);
    },0)
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleUrlChange() {
    if (!this.isSaving && this.hasUnsavedChanges()) {
      this.isSaving = true;
      this.tslLogic.postAllowances(this.clientEntity.sstRelationId!, this.allowanceEntity,false);
      this.isSaving = false;
    }
  }

  public hasUnsavedChanges(): boolean {
    return this.inkomenListFg.dirty || this.toeslagenFg.dirty || this.inkomenFg.dirty
  }

  disableFormGroup(formGroup: FormGroup | AbstractControl): void {
    if (formGroup instanceof FormGroup) {
      Object.keys(formGroup.controls).forEach((key) => {
        const control = formGroup.get(key);
        if (control instanceof FormControl) {
          control.disable();
        } else if (control instanceof FormGroup) {
          this.disableFormGroup(control);
        }
      });
    }
  }
}
